import { useState } from "react";
import {
    Box,
    Chip,
    Collapse,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { Search } from "lucide-react";

import CompactDataCard from "../../../components/common/CompactDataCard";
import PillFilterGroup from "../../../components/common/PillFilterGroup";
import IsoDateField from "../../../components/common/IsoDateField";
import {
    formatChannelLabel,
    getChannelChipStyles,
    getOrderStatusChipStyles
} from "../../../lib/presentation";
import type { OrdersFilterTraderOption } from "../../../lib/api/types";
import type { OrderRow } from "../../../types";
import { orderChannelFilters } from "../lib/helpers";

const orderValueFormatter = new Intl.NumberFormat("bg-BG", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
});

interface OrdersWorkspaceProps {
    channelFilter: (typeof orderChannelFilters)[number];
    dateFrom: string;
    dateTo: string;
    traderFilter: string;
    regionFilter: string;
    traderOptions: OrdersFilterTraderOption[];
    regionOptions: string[];
    showTraderFilter: boolean;
    onDateFromChange: (value: string) => void;
    onDateToChange: (value: string) => void;
    onTraderChange: (value: string) => void;
    onRegionChange: (value: string) => void;
    isCompact: boolean;
    lastUpdatedAt: string;
    onChannelChange: (value: (typeof orderChannelFilters)[number]) => void;
    onOpenOrderItems: (order: OrderRow) => void;
    onPageChange: (page: number) => void;
    onRowsPerPageChange: (rowsPerPage: number) => void;
    onSearchChange: (value: string) => void;
    onSelectOrder: (number: string) => void;
    onStatusChange: (value: string) => void;
    orderStatuses: string[];
    orders: OrderRow[];
    searchTerm: string;
    selectedOrderNumber?: string;
    statusFilter: string;
    unattributedCount: number;
    waitingForExpo: number;
    pagination: {
        page: number;
        rowsPerPage: number;
        total: number;
    };
}

export default function OrdersWorkspace({
                                            channelFilter,
                                            dateFrom,
                                            dateTo,
                                            traderFilter,
                                            regionFilter,
                                            traderOptions,
                                            regionOptions,
                                            showTraderFilter,
                                            onDateFromChange,
                                            onDateToChange,
                                            onTraderChange,
                                            onRegionChange,
                                            isCompact,
                                            /* lastUpdatedAt, */
                                            onChannelChange,
                                            onOpenOrderItems,
                                            onPageChange,
                                            onRowsPerPageChange,
                                            onSearchChange,
                                            onSelectOrder,
                                            onStatusChange,
                                            orderStatuses,
                                            orders,
                                            searchTerm,
                                            selectedOrderNumber,
                                            statusFilter,
                                            unattributedCount,
                                            waitingForExpo,
                                            pagination
                                        }: OrdersWorkspaceProps) {
    const [isExpanded, setIsExpanded] = useState(true);

    function getOrderRowKey(order: OrderRow, index: number) {
        return `${order.orderNumber}-${order.customer}-${order.createdAt}-${order.channel}-${index}`;
    }

    return (
        <Paper className="table-card" elevation={0}>
            <Box
                className="table-card__header"
                sx={{ cursor: "pointer" }}
                onClick={() => setIsExpanded((prev) => !prev)}
            >
                <Box>
                    <Typography variant="h5">Работен изглед на поръчките</Typography>
                    <Typography color="text.secondary">
                        Избери поръчка от таблицата, за да видиш връзката й с профила, статуса на синхрона и следващото действие.
                    </Typography>
                    {/* <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Данните от базата, обновени: {new Date(lastUpdatedAt).toLocaleString("bg-BG")}
                    </Typography> */}
                </Box>

                <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
                    <Chip label={`${waitingForExpo} чакат поток на едро`} variant="outlined" />
                    <Chip label={`${unattributedCount} без атрибуция`} variant="outlined" />
                    <IconButton size="small">
                        {isExpanded ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                </Stack>
            </Box>

            <Collapse in={isExpanded}>
                <Stack spacing={2} sx={{ mt: 2 }}>
                    <TextField
                        value={searchTerm}
                        onChange={(event) => onSearchChange(event.target.value)}
                        placeholder="Търси по номер, клиент, локация, кампания или синхрон"
                        fullWidth
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search size={18} />
                                    </InputAdornment>
                                )
                            }
                        }}
                    />

                    <Stack spacing={1.25}>
                        <PillFilterGroup
                            label="Статус"
                            options={["Всички", ...orderStatuses]}
                            value={statusFilter}
                            onChange={onStatusChange}
                        />
                        <PillFilterGroup
                            label="Канал"
                            options={orderChannelFilters}
                            renderLabel={formatChannelLabel}
                            value={channelFilter}
                            onChange={onChannelChange}
                        />
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                            <IsoDateField
                                label="От дата"
                                value={dateFrom}
                                onChange={onDateFromChange}
                                fullWidth
                            />
                            <IsoDateField
                                label="До дата"
                                value={dateTo}
                                onChange={onDateToChange}
                                fullWidth
                            />
                        </Stack>
                        {(showTraderFilter || regionOptions.length > 0) && (
                            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                                {showTraderFilter ? (
                                    <FormControl fullWidth size="small">
                                        <InputLabel id="orders-trader-filter">Търговец</InputLabel>
                                        <Select
                                            labelId="orders-trader-filter"
                                            label="Търговец"
                                            value={traderFilter}
                                            onChange={(event) => onTraderChange(event.target.value)}
                                        >
                                            <MenuItem value="Всички">Всички</MenuItem>
                                            {traderOptions.map((trader) => (
                                                <MenuItem key={trader.id} value={String(trader.id)}>
                                                    {trader.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                ) : null}
                                {regionOptions.length > 0 ? (
                                    <FormControl fullWidth size="small">
                                        <InputLabel id="orders-region-filter">Регион</InputLabel>
                                        <Select
                                            labelId="orders-region-filter"
                                            label="Регион"
                                            value={regionFilter}
                                            onChange={(event) => onRegionChange(event.target.value)}
                                        >
                                            <MenuItem value="Всички">Всички</MenuItem>
                                            {regionOptions.map((region) => (
                                                <MenuItem key={region} value={region}>
                                                    {region}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                ) : null}
                            </Stack>
                        )}
                    </Stack>

                    {isCompact ? (
                        <Stack spacing={1.5}>
                            {orders.map((row, index) => (
                                <CompactDataCard
                                    key={getOrderRowKey(row, index)}
                                    title={row.orderNumber}
                                    subtitle={`${row.customer} · ${row.location}`}
                                    selected={selectedOrderNumber === row.orderNumber}
                                    onClick={() => onSelectOrder(row.orderNumber)}
                                    badges={
                                        <>
                                            <Chip
                                                label={formatChannelLabel(row.channel)}
                                                size="small"
                                                sx={{
                                                    fontWeight: 700,
                                                    ...getChannelChipStyles(row.channel)
                                                }}

                                            />
                                            <Chip
                                                label={row.status}
                                                size="small"
                                                sx={{
                                                    fontWeight: 700,
                                                    ...getOrderStatusChipStyles(row.status)
                                                }}

                                            />
                                        </>
                                    }
                                >
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                        {orderValueFormatter.format(row.value)} €
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="primary.main"
                                        sx={{ fontWeight: 700, cursor: "pointer", width: "fit-content" }}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            onOpenOrderItems(row);
                                        }}
                                    >
                                        {row.customer}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Синхрон: {row.sync}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Кампания: {row.campaign}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Създадена: {row.createdAt}
                                    </Typography>
                                </CompactDataCard>
                            ))}

                            {orders.length === 0 && (
                                <Typography color="text.secondary">
                                    Няма поръчки за текущата комбинация от търсене и филтри.
                                </Typography>
                            )}
                        </Stack>
                    ) : (
                        <Box className="table-card__scroller">
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Поръчка</TableCell>
                                        <TableCell>Канал</TableCell>
                                        <TableCell>Клиент</TableCell>
                                        <TableCell>Локация</TableCell>
                                        <TableCell>Статус</TableCell>
                                        <TableCell align="right">Стойност</TableCell>
                                        <TableCell>Кампания</TableCell>
                                        <TableCell>Създадена</TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {orders.map((row, index) => (
                                        <TableRow
                                            key={getOrderRowKey(row, index)}
                                            hover
                                            selected={selectedOrderNumber === row.orderNumber}
                                            onClick={() => onSelectOrder(row.orderNumber)}
                                            sx={{
                                                cursor: "pointer",
                                                "& td": {
                                                    backgroundColor: selectedOrderNumber === row.orderNumber
                                                        ? "rgba(15, 118, 110, 0.06)"
                                                        : undefined
                                                }
                                            }}
                                        >
                                            <TableCell>
                                                <Typography sx={{ fontWeight: 700 }}>{row.orderNumber}</Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {row.sync}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={formatChannelLabel(row.channel)}
                                                    size="small"
                                                    sx={{
                                                        fontWeight: 700,
                                                        ...getChannelChipStyles(row.channel)
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography
                                                    sx={{
                                                        fontWeight: 700,
                                                        color: "primary.main",
                                                        width: "fit-content",
                                                        textDecoration: "underline",
                                                        textDecorationColor: "rgba(15, 118, 110, 0.35)",
                                                    }}
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        onOpenOrderItems(row);
                                                    }}
                                                >
                                                    {row.customer}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>{row.location}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={row.status}
                                                    size="small"
                                                    sx={{
                                                        fontWeight: 700,
                                                        ...getOrderStatusChipStyles(row.status)
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                {orderValueFormatter.format(row.value)} €
                                            </TableCell>
                                            <TableCell>{row.campaign}</TableCell>
                                            <TableCell>{row.createdAt}</TableCell>
                                        </TableRow>
                                    ))}

                                    {orders.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={8}>
                                                <Typography color="text.secondary">
                                                    Няма поръчки за текущата комбинация от търсене и филтри.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </Box>
                    )}

                    <TablePagination
                        component="div"
                        count={pagination.total}
                        page={pagination.page}
                        onPageChange={(_, nextPage) => onPageChange(nextPage)}
                        rowsPerPage={pagination.rowsPerPage}
                        onRowsPerPageChange={(event) => onRowsPerPageChange(Number(event.target.value))}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        labelRowsPerPage="Редове"
                    />
                </Stack>
            </Collapse>
        </Paper>
    );
}
